import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myShifts, setMyShifts] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myRes, openRes] = await Promise.all([
          api.get('/roster/my-shifts'),
          api.get('/roster/open-shifts'),
        ]);
        // API returns { items: [...] } or { data: [...] }
        setMyShifts(myRes.data.items || myRes.data.data || []);
        setOpenShifts(openRes.data.items || openRes.data.data || []);
      } catch (e) {
        console.error('Staff dashboard error:', e);
      }
    };
    fetchData();
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const todayShifts = myShifts.filter(s => s.date === today);
  const upcomingShifts = myShifts.filter(s => s.date >= today).slice(0, 5);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Welcome, {user?.full_name?.split(' ')[0]}</h1>
        <p className="text-muted-foreground mt-1">Here's your schedule overview</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/staff/roster')}>
          <CardContent className="p-4">
            <Calendar className="h-5 w-5 text-primary mb-2" />
            <p className="text-2xl font-bold tabular-nums">{myShifts.length}</p>
            <p className="text-xs text-muted-foreground">My Shifts</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/staff/open-shifts')}>
          <CardContent className="p-4">
            <MapPin className="h-5 w-5 text-[hsl(var(--gc-amber))] mb-2" />
            <p className="text-2xl font-bold tabular-nums">{openShifts.length}</p>
            <p className="text-xs text-muted-foreground">Open Shifts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Clock className="h-5 w-5 text-[hsl(var(--gc-sage))] mb-2" />
            <p className="text-2xl font-bold tabular-nums">{todayShifts.length}</p>
            <p className="text-xs text-muted-foreground">Today's Shifts</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Shifts */}
      {todayShifts.length > 0 && (
        <Card className="border-primary/20 bg-accent/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Today's Shifts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {todayShifts.map((shift) => (
                <div key={shift.id} className="flex items-center justify-between bg-card rounded-lg p-3 border">
                  <div>
                    <p className="font-medium">{shift.participant_name || 'Participant'}</p>
                    <p className="text-sm text-muted-foreground">{shift.start_time} - {shift.end_time} | {shift.service_name || 'Service'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={shift.status} />
                    <Button size="sm" variant="outline" onClick={() => navigate(`/staff/shift/${shift.id}`)} data-testid={`today-shift-action-${shift.id}`}>
                      {shift.status === 'ASSIGNED' ? 'Check In' : shift.status === 'IN_PROGRESS' ? 'Check Out' : 'View'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Shifts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Upcoming Shifts</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingShifts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No upcoming shifts</p>
          ) : (
            <div className="space-y-3">
              {upcomingShifts.map((shift) => (
                <div key={shift.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{shift.participant_name || 'Participant'}</p>
                    <p className="text-xs text-muted-foreground">{shift.date} | {shift.start_time}-{shift.end_time}</p>
                  </div>
                  <StatusBadge status={shift.status} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
